import { Controller } from "@hotwired/stimulus";

/**
 * The error screen's two behaviours: trying again, and noticing that the connection came back.
 *
 * Both are progressive. Retry is a button that reloads, which is what "try again" means for a page
 * the server rendered and got wrong; without script it is an inert button on a page whose other
 * two controls are ordinary links, so nobody is stranded. Back is an <a> to the home page in the
 * markup and only becomes `history.back()` here, so the fallback is the link itself rather than a
 * control that does nothing.
 *
 * The waiting label is the whole loading state. The system has no spinner and this screen is
 * allowed one moving thing; a button that says it is trying and cannot be pressed twice says the
 * same thing as a spinner and says it to a screen reader as well, through `aria-busy`.
 *
 * `online` is the browser's own answer to "is the connection back", and it is the only one worth
 * having: `navigator.onLine` is false only when the machine knows it is disconnected, so it is
 * read as a hint about what to show at rest and never as permission to stop listening.
 */
export default class extends Controller<HTMLElement> {
  static override targets = ["retry", "status"];

  static override values = {
    reconnectedLabel: String,
    /** How long the reconnected label is left up before the page reloads under it. */
    reloadAfter: { type: Number, default: 600 },
    retryingLabel: String,
  };

  declare readonly hasRetryTarget: boolean;
  declare readonly hasStatusTarget: boolean;
  declare readonly reconnectedLabelValue: string;
  declare readonly reloadAfterValue: number;
  declare readonly retryTarget: HTMLButtonElement;
  declare readonly retryingLabelValue: string;
  declare readonly statusTarget: HTMLElement;

  private readonly onOnline = (): void => this.reconnected();

  private timer: ReturnType<typeof setTimeout> | undefined;

  override connect(): void {
    window.addEventListener("online", this.onOnline);
  }

  override disconnect(): void {
    window.removeEventListener("online", this.onOnline);

    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
  }

  back(event: Event): void {
    /* `length > 1` is the only thing a page can ask: the entries themselves are not readable, and
       a tab opened straight onto this URL has exactly one. With no history to go back to, the
       link's own href - the home page - is where the reader should end up, so the event is left
       alone. */
    if (window.history.length <= 1) {
      return;
    }

    event.preventDefault();
    window.history.back();
  }

  retry(): void {
    this.busy();

    window.location.reload();
  }

  private busy(): void {
    if (!this.hasRetryTarget) {
      return;
    }

    this.retryTarget.setAttribute("aria-busy", "true");
    this.retryTarget.disabled = true;

    if (this.retryingLabelValue !== "") {
      this.retryTarget.textContent = this.retryingLabelValue;
    }
  }

  private reconnected(): void {
    if (this.hasStatusTarget) {
      this.statusTarget.classList.add("wp-status-active");

      if (this.reconnectedLabelValue !== "") {
        this.statusTarget.textContent = this.reconnectedLabelValue;
      }
    }

    this.busy();

    /* A beat before the reload, so the reader sees that it reconnected rather than watching the
       page blink for no stated reason. */
    this.timer = setTimeout(
      () => window.location.reload(),
      this.reloadAfterValue,
    );
  }
}
